import { useMemo, useState } from "react";
import { AppWindow, ChevronDown, RefreshCw, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { GpConnectRecordDialog } from "./GpConnectRecordDialog";
import { cn } from "@/lib/utils";
import { accessRecord } from "@/dispatchers/patientDispatchers";
import useTitanApp from "@/hooks/useTitanApp";
import { mapAccessRecordToGpConnectRecord } from "./GpConnectRecordModel";

interface NhsServicesMenuProps {
  className?: string;
  align?: "start" | "center" | "end";
  nhsNumber?: string;
  requestingOdsCode?: string;
}

export function NhsServicesMenu({
  className,
  align = "end",
  nhsNumber,
  requestingOdsCode,
}: NhsServicesMenuProps) {
  const [gpConnectOpen, setGpConnectOpen] = useState(false);
  const { appState, patientState } = useTitanApp();
  const { features } = appState || {};
  const { ifNHS111ServiceEnabled } = features;
  const { accessRecordData, isAccessRecordLoading } = patientState || {};
  if (!ifNHS111ServiceEnabled) {
    return null;
  }

  const gpConnectRecord = useMemo(
    () => mapAccessRecordToGpConnectRecord(accessRecordData),
    [accessRecordData],
  );

  const handleGpConnectOpen = async () => {
    if (nhsNumber && requestingOdsCode) {
      await accessRecord({ nhsNumber, requestingOdsCode });
    }
    setGpConnectOpen(true);
  };

  return (
    <>
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={cn("gap-1.5", className)}
          aria-label="Open NHS services"
        >
          <span
            className="inline-flex items-center rounded-sm bg-[#005EB8] px-1.5 py-0.5 text-[11px] font-bold italic leading-none tracking-tight text-white"
            aria-hidden="true"
          >
            NHS
          </span>
          NHS services
          <ChevronDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56">
        <DropdownMenuLabel>NHS services</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2 [&_svg]:size-4 [&_svg]:shrink-0">
          <AppWindow />
          NCRS
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-2 [&_svg]:size-4 [&_svg]:shrink-0">
          <AppWindow />
          Prescription Tracker
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="gap-2 [&_svg]:size-4 [&_svg]:shrink-0"
          onSelect={handleGpConnectOpen}
        >
          <Stethoscope />
          GP Connect
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-2 [&_svg]:size-4 [&_svg]:shrink-0">
          <RefreshCw />
          PDS Sync
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <GpConnectRecordDialog
      open={gpConnectOpen}
      onOpenChange={setGpConnectOpen}
      record={gpConnectRecord}
      loading={isAccessRecordLoading}
    />
    </>
  );
}
